import React, { useRef, useState } from 'react';
import { Box } from '@mui/material';

const fileInputStyle: React.CSSProperties = {
    width: '100%',
    height: '100%',
    position: 'absolute',
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    opacity: 0,
    cursor: 'pointer',
    zIndex: 10,
};

type UploaderFiles = FileList | File[];

type UploaderTemplateProps = {
    children: React.ReactNode;
    isDragging: boolean;
};

const UploaderTemplate = ({ children, isDragging }: UploaderTemplateProps) => {
    return (
        <Box
            sx={{
                width: '100%',
                minHeight: { xs: 76, sm: 88 },
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: 2.5,
                px: { xs: 1.25, sm: 1.75 },
                py: { xs: 1.15, sm: 1.35 },
                boxSizing: 'border-box',
                position: 'relative',
                overflow: 'hidden',
                border: '1px dashed',
                borderColor: isDragging ? 'primary.main' : 'divider',
                backgroundColor: isDragging ? 'action.hover' : 'transparent',
                cursor: 'pointer',
                transition:
                    'border-color .16s ease, background-color .16s ease',
                '&:hover': {
                    borderColor: 'primary.main',
                    backgroundColor: 'action.hover',
                },
            }}>
            {children}
        </Box>
    );
};

const UploaderInput = ({
    onChangeFiles,
}: {
    onChangeFiles: (files: UploaderFiles) => void;
}) => {
    const inputRef = useRef<HTMLInputElement | null>(null);

    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const files = event.target.files;
        if (files?.length) {
            onChangeFiles(files);
        }

        if (inputRef.current) {
            inputRef.current.value = '';
        }
    };

    return (
        <input
            ref={inputRef}
            type="file"
            multiple
            accept=".png,.jpeg,.jpg,.zip,.rar,.doc,.docx,.xls,.xlsx,.pdf,.txt"
            aria-label="انتخاب فایل برای پیوست تیکت"
            onChange={handleInputChange}
            style={fileInputStyle}
        />
    );
};

const UploaderField = (props: {
    children: React.ReactNode;
    onChangeFiles: (files: UploaderFiles) => void;
}) => {
    const [isDragging, setIsDragging] = useState(false);

    const handleDragOver = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(true);
    };

    const handleDragLeave = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);

        const files = Array.from(e.dataTransfer.files);
        if (files.length) {
            props.onChangeFiles(files);
        }
    };

    return (
        <Box
            sx={{
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                position: 'relative',
            }}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}>
            <UploaderTemplate isDragging={isDragging}>
                <UploaderInput onChangeFiles={props.onChangeFiles} />
                {props.children}
            </UploaderTemplate>
        </Box>
    );
};

export { UploaderField };
